import React, { useState } from 'react';
import { Button, Modal, Space, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { SubsystemDetail } from '../../../services/ai-admin-api';
import { PublishConfirmModal } from '../shared/PublishConfirmModal';

const { Text } = Typography;

interface Props {
    detail: SubsystemDetail | null;
    isDirty: boolean;
    isSaving: boolean;
    isPublishing: boolean;
    isDiscarding: boolean;
    onSaveDraft: () => Promise<void> | void;
    onDiscardDraft: () => Promise<void> | void;
}

/**
 * Header action row used by every Phase 3 ConfigurePage. Renders Save Draft /
 * Publish / Discard Draft with the right disabled states and a confirmation
 * for destructive actions. Publish opens PublishConfirmModal which loads the
 * structured diff first.
 */
export const SubsystemDraftActions: React.FC<Props> = ({
    detail,
    isDirty,
    isSaving,
    isPublishing,
    isDiscarding,
    onSaveDraft,
    onDiscardDraft,
}) => {
    const [publishOpen, setPublishOpen] = useState(false);
    const hasDraft = !!detail?.hasDraft;

    const handleDiscard = () => {
        Modal.confirm({
            title: 'Discard draft?',
            icon: <ExclamationCircleOutlined />,
            content: 'Unsaved edits will be lost. The currently-published version stays unchanged.',
            okText: 'Discard',
            okButtonProps: { danger: true },
            onOk: onDiscardDraft,
        });
    };

    return (
        <>
            <Space wrap>
                {hasDraft && <Tag color="orange">Draft pending</Tag>}
                {isDirty && <Tag color="gold">Unsaved edits</Tag>}
                <Button
                    icon={<SaveOutlined />}
                    onClick={() => onSaveDraft()}
                    disabled={!isDirty}
                    loading={isSaving}
                >
                    Save draft
                </Button>
                <Tooltip
                    title={
                        !hasDraft
                            ? 'Save a draft first.'
                            : isDirty
                                ? 'Save your edits before publishing.'
                                : ''
                    }
                >
                    <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        disabled={!hasDraft || isDirty}
                        loading={isPublishing}
                        onClick={() => setPublishOpen(true)}
                    >
                        Publish
                    </Button>
                </Tooltip>
                {hasDraft && (
                    <Button
                        danger
                        loading={isDiscarding}
                        onClick={handleDiscard}
                    >
                        Discard draft
                    </Button>
                )}
            </Space>

            <PublishConfirmModal
                open={publishOpen}
                detail={detail}
                onCancel={() => setPublishOpen(false)}
            />
        </>
    );
};
